import { map } from 'rxjs/operators';
import { tap, finalize } from 'rxjs';
import { SubscriptionInterface } from '@/app/features/subscription/interfaces';
import { SubscriptionApiService, SubscriptionIntervalEnum } from '@/app/features/subscription';

import { signal, Injectable, WritableSignal } from '@angular/core';

import { CurrentSubscriptionInterface } from '@/app/features/subscription/interfaces/current-subscription.interface';
import { GetCheckoutUrlPayloadInterface } from '@/app/features/subscription/interfaces/get-checkout-url-payload.interface';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionStore {
  public isLoading: WritableSignal<boolean> = signal(false);

  public selectedInterval: WritableSignal<SubscriptionIntervalEnum> = signal(SubscriptionIntervalEnum.Monthly);

  public currentPlan: WritableSignal<CurrentSubscriptionInterface | undefined> = signal(undefined);

  public availablePlans: WritableSignal<Record<string, SubscriptionInterface[]>> = signal({});

  constructor(private apiService: SubscriptionApiService) {}

  public loadCurrentPlan() {
    this.isLoading.set(true);

    return this.apiService.loadCurrentPlan().pipe(
      tap((response) => this.currentPlan.set(response.payload.content)),
      finalize(() => this.isLoading.set(false)),
    );
  }

  public getCheckoutUrl(payload: GetCheckoutUrlPayloadInterface) {
    return this.apiService.getCheckoutUrl(payload);
  }

  public cancelSubscription(plan: CurrentSubscriptionInterface) {
    return this.apiService.cancelSubscription(plan);
  }

  public renewSubscription(plan: CurrentSubscriptionInterface) {
    return this.apiService.renewSubscription(plan);
  }

  public updateSubscription(plan: SubscriptionInterface | null = null) {
    return this.apiService.updateSubscription(plan);
  }

  public loadAvailablePlans(currentPriceId = null) {
    this.isLoading.set(true);

    return this.apiService.loadAvailablePlans().pipe(
      map((response) => {
        const content = (response.payload.content || []).filter((plan) => {
          return plan.active || currentPriceId === plan.price_id;
        });

        return content.reduce((grouped: Record<string, SubscriptionInterface[]>, plan: SubscriptionInterface) => {
          const interval = plan.interval;
          (grouped[interval] = grouped[interval] || []).push(plan);
          return grouped;
        }, {});
      }),
      tap((grouped) => this.availablePlans.set(grouped)),
      finalize(() => this.isLoading.set(false)),
    );
  }
}
